import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  Trophy,
  Clock,
  HelpCircle,
  BarChart2,
  XCircle,
  CheckCircle,
  TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const StopSignalChallenge = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // -------------------------------
  //        GAME STATE
  // -------------------------------
  const [gameMode, setGameMode] = useState("intro");
  const [difficulty, setDifficulty] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameWasCompleted, setGameWasCompleted] = useState(false);

  // Overall scoring & performance
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);

  // Main performance metrics
  const [goReactionTimes, setGoReactionTimes] = useState([]);
  const [stopSuccessCount, setStopSuccessCount] = useState(0);
  const [stopFailCount, setStopFailCount] = useState(0);
  const [goCount, setGoCount] = useState(0);
  const [omissionCount, setOmissionCount] = useState(0);

  // Optional start/end times
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);

  // UI / tutorial
  const [showInstructions, setShowInstructions] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  // Timer
  const [timeLeft, setTimeLeft] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);

  // Adaptive Stop-Signal Delay
  const [stopSignalDelay, setStopSignalDelay] = useState(250);

  // Trials + indexing
  const [trials, setTrials] = useState([]);
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);

  // “Go” stimulus time
  const goSignalTimeRef = useRef(0);

  // Has user responded
  const [hasResponded, setHasResponded] = useState(false);

  // Visual states
  const [showStopSignal, setShowStopSignal] = useState(false);
  const [currentCue, setCurrentCue] = useState(null);

  // Timeout refs
  const finalizationTimeoutRef = useRef(null);
  const stopSignalTimeoutRef = useRef(null);

  // ---------------
  //  FETCH SCORES w/ Tie-Break Sorting
  // ---------------

  useEffect(() => {
    if (gameMode === "play" && gameStarted) {
      startGame();
    }
  }, [gameMode, gameStarted]);

  useEffect(() => {
    if (isPlaying && trials.length > 0) {
      // Start the first trial only after the state update for isPlaying
      console.log("[useEffect] isPlaying -> runTrial(0)");
      runTrial(0, trials);
    }
  }, [isPlaying, trials]);

  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // Reference the single leaderboard document for the "stop-signal" game
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "stop-signal"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Retrieve scores for the current difficulty (key as a string)
          const scores =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // ---------------
  //   TIMER SETUP
  // ---------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Generate random trials
  const generateTrials = useCallback((numTrials) => {
    const stopProportion = 0.25;
    const arr = [];
    for (let i = 0; i < numTrials; i++) {
      arr.push({
        isStop: Math.random() < stopProportion,
        cue: "◉",
      });
    }
    return arr;
  }, []);

  // ---------------
  //  START GAME
  // ---------------
  const startGame = () => {
    console.log("[startGame] Initializing game...");
    // Reset
    setScore(0);
    setGoReactionTimes([]);
    setStopSuccessCount(0);
    setStopFailCount(0);
    setGoCount(0);
    setOmissionCount(0);

    setShowStopSignal(false);
    setCurrentCue(null);

    setGameStartTime(Date.now());
    setGameEndTime(null);

    // Number of trials
    const numTrials = 40;
    const newTrials = generateTrials(numTrials);
    setTrials(newTrials);
    setCurrentTrialIndex(0);

    // Set SSD by difficulty
    let initialSSD = 250;
    if (difficulty === 2) initialSSD = 200;
    if (difficulty === 3) initialSSD = 150;
    setStopSignalDelay(initialSSD);

    // Reset timer
    setTimeLeft(120);

    // Finally mark the game as playing
    setIsPlaying(true);
  };

  // ---------------
  //  END GAME
  // ---------------
  const endGame = () => {
    console.log("[endGame] Called. Stopping game, clearing timers...");
    setIsPlaying(false);
  
    clearInterval(timerRef.current);
    if (finalizationTimeoutRef.current) {
      clearTimeout(finalizationTimeoutRef.current);
      finalizationTimeoutRef.current = null;
    }
    if (stopSignalTimeoutRef.current) {
      clearTimeout(stopSignalTimeoutRef.current);
      stopSignalTimeoutRef.current = null;
    }
  
    setGameEndTime(Date.now());
    setGameMode("results");
  
    // ✅ Only allow saving score if the player finished all trials
    const gameWasFullyCompleted = currentTrialIndex + 1 >= trials.length;
    setGameWasCompleted(gameWasFullyCompleted);
  };
  

  // ---------------
  //   RUN TRIAL
  // ---------------
  const runTrial = (index, trialList) => {
    console.log("[runTrial] Called with index =", index);

    if (!isPlaying) {
      console.log("[runTrial] isPlaying=false, aborting.");
      return;
    }
    if (!trialList[index]) {
      console.log("[runTrial] No more trials, endGame().");
      endGame();
      return;
    }

    setHasResponded(false);
    setCurrentTrialIndex(index);

    const trial = trialList[index];
    console.log("[runTrial] Current trial:", trial);

    // Show “Go”
    setCurrentCue(trial.cue);
    goSignalTimeRef.current = Date.now();

    if (trial.isStop) {
      console.log(
        `[runTrial] STOP trial -> scheduling STOP at SSD=${stopSignalDelay}ms`
      );
      stopSignalTimeoutRef.current = setTimeout(() => {
        if (!isPlaying) return;
        if (!hasResponded) {
          console.log("[runTrial] Displaying STOP signal.");
          setShowStopSignal(true);
        }
      }, stopSignalDelay);
    } else {
      setShowStopSignal(false);
    }

    console.log(
      "[runTrial] Scheduling finalizeTrial in 1500ms if no response."
    );
    finalizationTimeoutRef.current = setTimeout(() => {
      if (!isPlaying) return;
      if (hasResponded) {
        console.log(
          "[runTrial / 1500ms] user already responded, skip finalize."
        );
        return;
      }
      console.log("[runTrial / 1500ms] No response => omission finalize.");
      finalizeTrial(index, trialList, false);
    }, 1500);
  };

  // ---------------
  //  FINALIZE TRIAL
  // ---------------
  const finalizeTrial = (index, trialList, userResponded) => {
    console.log(
      "[finalizeTrial] index=",
      index,
      "userResponded=",
      userResponded
    );

    // Clear timeouts
    if (finalizationTimeoutRef.current) {
      clearTimeout(finalizationTimeoutRef.current);
      finalizationTimeoutRef.current = null;
    }
    if (stopSignalTimeoutRef.current) {
      clearTimeout(stopSignalTimeoutRef.current);
      stopSignalTimeoutRef.current = null;
    }

    setShowStopSignal(false);

    const trial = trialList[index];
    if (!trial) {
      console.log("[finalizeTrial] No trial found, likely end of list.");
      return;
    }

    if (trial.isStop) {
      if (!userResponded) {
        // STOP success: +10 points and increase SSD by 50ms
        console.log("[finalizeTrial] STOP success => +10, inc SSD");
        setStopSuccessCount((prev) => prev + 1);
        setScore((prev) => prev + 10);
        setStopSignalDelay((prev) => prev + 50);
      } else {
        // STOP fail: -10 points and decrease SSD by 50ms
        console.log("[finalizeTrial] STOP fail => -10, dec SSD");
        setStopFailCount((prev) => prev + 1);
        setScore((prev) => (prev > 0 ? prev - 10 : 0));
        setStopSignalDelay((prev) => Math.max(50, prev - 50));
      }
    } else {
      // GO trial: if correct, add +20 points; omissions get no points.
      if (!userResponded) {
        console.log("[finalizeTrial] GO omission => no response");
        setOmissionCount((prev) => prev + 1);
      }
    }

    // Schedule the next trial after a short ITI.
    setTimeout(() => {
      if (!isPlaying) return;
      const nextIndex = index + 1;
      if (!trialList[nextIndex]) {
        console.log("[finalizeTrial] No more trials => endGame()");
        endGame();
      } else {
        console.log("[finalizeTrial] runTrial(nextIndex) =>", nextIndex);
        runTrial(nextIndex, trialList);
      }
    }, 300);
  };

  // ---------------
  //  HANDLE USER RESPONSE
  // ---------------
  const handleUserResponse = () => {
    console.log("[handleUserResponse] Called.");

    if (!isPlaying) return;
    if (hasResponded) return;

    setHasResponded(true);
    if (finalizationTimeoutRef.current) {
      clearTimeout(finalizationTimeoutRef.current);
      finalizationTimeoutRef.current = null;
    }

    const rt = Date.now() - goSignalTimeRef.current;
    console.log(`[handleUserResponse] ReactionTime=${rt}ms`);

    const trial = trials[currentTrialIndex];
    if (!trial) return;

    if (!trial.isStop) {
      // GO correct: +20 points instead of +10.
      console.log("[handleUserResponse] GO correct => +20");
      setScore((prev) => prev + 20);
      setGoReactionTimes((prev) => [...prev, rt]);
      setGoCount((prev) => prev + 1);
    } else {
      console.log(
        "[handleUserResponse] STOP fail => finalize with userResponded=true"
      );
    }

    finalizeTrial(currentTrialIndex, trials, true);
  };

  // ---------------
  //  SAVE SCORE
  // ---------------
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // ⏱️ Calculate avgGoRT, finalSSD, SSRT
      const avgGoRT =
        goReactionTimes.length > 0
          ? Math.round(
              goReactionTimes.reduce((a, b) => a + b, 0) /
                goReactionTimes.length
            )
          : 0;

      const finalSSD = stopSignalDelay;
      const estimatedSSRT = avgGoRT - stopSignalDelay;

      // 🧠 Resolve userName with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "stop-signal",
        gameId: "stop-signal",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        stopSuccessCount,
        stopFailCount,
        omissionCount,
        avgGoRT,
        finalSSD,
        estimatedSSRT,
        totalTrials: trials.length,
        timestamp: new Date(),
      };

      // 🔗 Firebase references
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "stop-signal"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Sorting: score > SSD > lower SSRT
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.finalSSD !== b.finalSSD) return b.finalSSD - a.finalSSD;
        if (a.estimatedSSRT !== b.estimatedSSRT)
          return a.estimatedSSRT - b.estimatedSSRT;
        return a.timestamp - b.timestamp;
      };

      // 🔁 Transaction to update leaderboard + user
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const difficultyKey = difficulty.toString();
        let currentLeaderboard =
          leaderboardData.leaderboards[difficultyKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[difficultyKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 💾 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset game state
      setGameMode("intro");
      setGameStarted(false);
    } catch (error) {
      console.error("Error saving Stop-Signal score:", error);
    }
  };

  // ---------------
  //  TUTORIAL
  // ---------------
  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // ---------------
  //   RENDER
  // ---------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-blue-300">
              Stop Signal Challenge
            </h1>
            <div className="p-2 bg-blue-500/10 rounded-full">
              <Brain className="text-blue-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Brain className="text-blue-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Stop Signal Challenge Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6 text-slate-300">
                  <p>
                    The Stop-Signal Task measures response inhibition. You’ll
                    see a “Go” cue to respond to as quickly as possible, but on
                    certain trials, a Stop signal appears after a short delay.
                    When you see the Stop signal, your job is to withhold your
                    response.
                  </p>
                  <ul className="list-disc ml-4 space-y-2">
                    <li>
                      <span className="font-medium">Go Trials:</span> Press the
                      button quickly.
                    </li>
                    <li>
                      <span className="font-medium">Stop Trials:</span> Try to
                      withhold your response if a stop signal appears.
                    </li>
                    <li>
                      The delay between the Go cue and Stop signal (SSD) adapts
                      in real-time to maintain ~50% success rate.
                    </li>
                  </ul>
                  <p>
                    This task is widely used in clinical and research settings
                    to measure impulse control and is beneficial for
                    strengthening inhibitory control circuits in the frontal
                    lobes.
                  </p>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-cyan-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Stop Signal Challenge
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Practice your impulse control and response inhibition in this
                  lab-validated Stop-Signal Task.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <TrendingUp size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Impulse Control
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Strengthen inhibitory circuits by learning to stop on cue.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">5-10 minutes</h3>
                  <p className="text-sm text-slate-300 text-center">
                    A quick, high-intensity training session.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">High Scores</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Track your best inhibitory performance.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => setDifficulty(lvl)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === lvl
                            ? "bg-gradient-to-r from-blue-500 to-cyan-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => setGameMode("play")}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            SSD
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((s, index) => {
                          let formattedName = s.userName;
                          if (s.userName && s.userName.includes(" ")) {
                            const parts = s.userName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={s.id}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {s.finalSSD ? `${s.finalSSD} ms` : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-center text-blue-300 font-medium">
                                {s.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Stop-Signal Task
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-4 text-slate-300">
                  {tutorialStep === 0 && (
                    <>
                      <p>
                        In this challenge, you’ll see a cue (like a symbol or
                        shape) prompting you to press the “Respond” button.
                        That’s a “Go” trial. Your job is to press quickly.
                      </p>
                      <p>
                        Sometimes a loud beep or visual “STOP” will appear after
                        a short delay. If you see or hear this, do NOT press the
                        button. That’s a “Stop” trial.
                      </p>
                    </>
                  )}
                  {tutorialStep === 1 && (
                    <>
                      <p>
                        The time between the Go cue and the Stop signal (called
                        SSD) changes throughout the game.
                      </p>
                      <ul className="list-disc ml-4 space-y-2">
                        <li>
                          If you successfully stop, the task becomes harder by
                          increasing SSD.
                        </li>
                        <li>
                          If you fail to stop, the SSD decreases to make it
                          easier next time.
                        </li>
                      </ul>
                    </>
                  )}
                  {tutorialStep === 2 && (
                    <>
                      <p>
                        This design ensures about half the Stop trials are
                        successful and half fail. This measure is key to
                        estimating your Stop-Signal Reaction Time (SSRT), a core
                        measure of impulsivity in clinical research.
                      </p>
                      <p>
                        Press “Next” for final instructions and then let's play.
                      </p>
                    </>
                  )}
                  {tutorialStep === 3 && (
                    <>
                      <p>
                        Summary of the rules:
                        <ul className="list-disc ml-6 mt-2">
                          <li>
                            Press <strong>Respond</strong> quickly on Go trials.
                          </li>
                          <li>
                            If you see a <strong>Stop</strong> signal, try NOT
                            to respond.
                          </li>
                          <li>
                            The <strong>Stop Signal Delay</strong> changes so
                            that you succeed about half the time.
                          </li>
                          <li>
                            You earn points for correct Go responses and correct
                            Stop inhibitions.
                          </li>
                        </ul>
                      </p>
                      <p className="mt-2">
                        Ready? Let’s begin the actual game!
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <button
                    onClick={() => setGameStarted(true)}
                    className="py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors"
                  >
                    Start Game
                  </button>
                </div>
              )}

              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                  <span className="text-blue-300 font-medium">
                    Difficulty: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Stimulus display */}
                <div className="relative flex flex-col items-center justify-center w-64 h-64 bg-slate-900/60 border border-slate-700 rounded-xl">
                  <AnimatePresence exitBeforeEnter>
                    {currentCue && (
                      <motion.div
                        // Use both the index and cue in the key so each new cue triggers an animation
                        key={`${currentTrialIndex}-${currentCue}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                        className="text-4xl text-white"
                      >
                        {currentCue}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {showStopSignal && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div className="text-red-500 text-5xl font-extrabold">
                          STOP
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Controls & progress */}
                <div className="md:w-1/3 space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Trial Progress
                    </h3>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-sm">Trial</span>
                      <span className="text-white font-medium">
                        {currentTrialIndex + 1}/{trials.length}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                        style={{
                          width: `${
                            (currentTrialIndex / trials.length) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <button
                      onClick={handleUserResponse}
                      className="w-full py-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={20} />
                      Respond
                    </button>
                  </div>

                  <div>
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Session Complete!
                </h2>
                <p className="text-slate-300">
                  Your Stop-Signal session has ended.
                </p>
              </div>

              {/* Explanation of metrics */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="text-green-400 font-medium">
                    Stop Success
                  </span>
                  : No response on a Stop trial = Correct Inhibition (+5
                  points).
                  <br />
                  <span className="text-red-400 font-medium">Stop Fail</span>:
                  You responded on a Stop trial = Failed Inhibition (-5 points).
                  <br />
                  <span className="text-amber-400 font-medium">
                    Go Omission
                  </span>
                  : You did not respond on a Go trial (0 points, missed
                  opportunity).
                  <br />
                  <br />
                  <span className="text-blue-400 font-medium">
                    Average Go RT (ms)
                  </span>
                  : The average reaction time on Go trials. Lower indicates
                  faster responses.
                  <br />
                  <span className="text-cyan-400 font-medium">
                    Final SSD (ms)
                  </span>
                  : The final stop-signal delay. A higher SSD means the Stop
                  signal was appearing later, indicating better inhibitory
                  control.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Results summary */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-blue-400" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-blue-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Stop Successes</span>
                      <span className="text-green-400">{stopSuccessCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Stop Failures</span>
                      <span className="text-red-400">{stopFailCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Go Omission</span>
                      <span className="text-amber-400">{omissionCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Average Go RT (ms)</span>
                      <span className="text-white">
                        {goReactionTimes.length > 0
                          ? Math.round(
                              goReactionTimes.reduce((a, b) => a + b, 0) /
                                goReactionTimes.length
                            )
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Final SSD (ms)</span>
                      <span className="text-white">{stopSignalDelay}</span>
                    </div>
                  </div>
                </div>

                {/* Performance Analysis */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-blue-400" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Stop success rate */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">
                          Stop Success Rate
                        </span>
                        <span className="text-white text-sm">
                          {stopSuccessCount + stopFailCount > 0
                            ? `${Math.round(
                                (stopSuccessCount /
                                  (stopSuccessCount + stopFailCount)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                          style={{
                            width:
                              stopSuccessCount + stopFailCount > 0
                                ? `${
                                    (stopSuccessCount /
                                      (stopSuccessCount + stopFailCount)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Trains frontal-lobe inhibitory pathways
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Improves impulse control
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Increases cognitive flexibility
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameWasCompleted && (
  <button
    onClick={saveScore}
    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
  >
    <Trophy size={18} />
    Save Score
  </button>
)}


                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StopSignalChallenge;
